import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Progress, Spinner, Text } from "@athena/ui";
import { ChevronRight, PenLine } from "lucide-react";
import { BULK_UPLOAD_PATH, useUploadQueue } from "./uploadQueueContext";

/**
 * A pill in the corner while the queue has work, anywhere but the uploader itself.
 *
 * Uploads keep running when the operator leaves the page (see `UploadQueueProvider`), and without
 * this nothing on screen would say so — or that forty videos are still waiting for a description.
 */
export function UploadQueueIndicator() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { summary } = useUploadQueue();

    const uploading = summary.transferring;
    if (pathname === BULK_UPLOAD_PATH || (uploading === 0 && summary.needsDetails === 0)) return null;

    return (
        <button
            type="button"
            onClick={() => navigate(BULK_UPLOAD_PATH)}
            className="border-border bg-card hover:border-primary/50 focus-visible:ring-ring fixed right-6 bottom-6 z-50 flex w-72 items-center gap-3 rounded-2xl border p-3 text-left shadow-lg transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
            <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                {uploading > 0 ? <Spinner className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-1">
                <Text as="span" size="sm" weight="medium" className="truncate">
                    {uploading > 0
                        ? t("admin.bulkUpload.indicatorUploading", { count: uploading })
                        : t("admin.bulkUpload.indicatorNeedsDetails", { count: summary.needsDetails })}
                </Text>
                {uploading > 0 ? (
                    <Progress value={Math.round(summary.transferFraction * 100)} className="h-1.5" aria-hidden />
                ) : null}
                {uploading > 0 && summary.needsDetails > 0 && (
                    <Text as="span" size="xs" muted className="truncate">
                        {t("admin.bulkUpload.indicatorNeedsDetails", { count: summary.needsDetails })}
                    </Text>
                )}
            </span>
            <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
        </button>
    );
}
